import React, { useState, useEffect, useCallback } from 'react';
import { PiSortAscendingLight, PiSortDescendingLight } from "react-icons/pi";
import axios from 'axios';
import { CiSearch } from "react-icons/ci";
import "react-datepicker/dist/react-datepicker.css";
import { format, isWithinInterval, parseISO } from 'date-fns';
import { FaFileExcel, FaFilePdf } from 'react-icons/fa';
import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';
import "jspdf-autotable";
import { BaseUrl } from '../App';
import { Circles } from 'react-loader-spinner';
import { FadeLoader } from 'react-spinners';


function Client_pay_3() {
    const [tableData, setTableData] = useState([]);
    const [sortedTable, setSortedTable] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });
    const [searchQuery, setSearchQuery] = useState("");
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [startDate, setStartDate] = useState(null);
    // const [loading, setLoading] = useState(false);/

    const [endDate, setEndDate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

    useEffect(() => {
        let timer;
        if (error) {
          timer = setTimeout(() => {
            setError("");
          }, 5000); // 5 seconds
        }
        return () => clearTimeout(timer);
      }, [error]);

    useEffect(() => {
        fetchData();

        // Add window resize event listener for responsive design
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchData = useCallback(async (retryCount = 0) => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.post(`${BaseUrl}/backend/api/payment_by_user_type.php`, {
                action: "get_all_recurrence_data",
                api_key: "1"
            });
    
            if (response.data.status === "true" && Array.isArray(response.data.response)) {
                setTableData(response.data.response);
                setSortedTable(response.data.response);
            } else {
                setError("No recurrence data available or invalid response format");
                setTableData([]);
            }
        } catch (err) {
            console.error("Error fetching data:", err);
            if (retryCount < 3) {
                console.log(`Retrying... Attempt ${retryCount + 1}`);
                setTimeout(() => fetchData(retryCount + 1), 1000 * (retryCount + 1));
            } else {
                setError("Network error. Please check your connection and try again.");
                setTableData([]);
            }
        }  finally {
      setLoading(false);
    }
    }, []);

    const handleSort = (column) => {
        let direction = 'asc';
        if (sortConfig.key === column && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key: column, direction });

        const sortedData = [...sortedTable].sort((a, b) => {
            if (a[column] < b[column]) return direction === 'asc' ? -1 : 1;
            if (a[column] > b[column]) return direction === 'asc' ? 1 : -1;
            return 0;
        });

        setSortedTable(sortedData);
    };

    const getSortIcon = (column) => {
        if (sortConfig.key !== column) {
            return <PiSortAscendingLight className="text-gray-500" />;
        }
        return sortConfig.direction === 'asc' ? <PiSortAscendingLight /> : <PiSortDescendingLight />;
    };

    const handleSearch = (e) => setSearchQuery(e.target.value);

    const handleEntriesChange = (e) => {
        setEntriesPerPage(e.target.value === "All" ? "All" : parseInt(e.target.value, 10));
        setCurrentPage(1);
    };

    const handleDateChange = (dates) => {
        const [start, end] = dates;
        setStartDate(start);
        setEndDate(end);
    };

    const filteredData = sortedTable.filter((row) => {
        const matchesSearch = Object.values(row).some(value => 
            value && value.toString().toLowerCase().includes(searchQuery.toLowerCase())
        );

        const isInDateRange = (!startDate || !endDate) ? true : 
            (row.status && isWithinInterval(new Date(row.status), { start: startDate, end: endDate }));

        return matchesSearch && isInDateRange;
    });

    const startIndex = (currentPage - 1) * entriesPerPage;
    const paginatedData = entriesPerPage === "All"
        ? filteredData
        : filteredData.slice(startIndex, startIndex + (entriesPerPage === "All" ? filteredData.length : entriesPerPage));

    const totalPages = Math.ceil(filteredData.length / (entriesPerPage === "All" ? 1 : entriesPerPage));

    const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
    const handleNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));
    const handlePageChange = (page) => setCurrentPage(page);

    const handleExcelExport = () => {
        const columns = ['S.No','Recurring ID', 'Client', 'Recurrence Type',];
        const rows = filteredData.map((row, index) => [
            index + 1,
            row.recurring_id,
            row.client_name,
            row.recurrence_type,
            // row.status
        ]);

        const ws = XLSX.utils.aoa_to_sheet([columns, ...rows]);  
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Recurrence Data');
        XLSX.writeFile(wb, 'recurrence-data.xlsx');
    };

    const handlePdfExport = () => {
        const doc = new jsPDF();
        const columns = ['S.No','Recurring ID', 'Client', 'Recurrence Type'];
        const rows = filteredData.map((row, index) => [
            index + 1,
            row.recurring_id,
            row.client_name,
            row.recurrence_type,
            // row.status
        ]);

        doc.autoTable({
            head: [columns],
            body: rows,
            startY: 20,
            theme: 'grid',
            margin: { horizontal: 10 },
            headStyles: {
                fillColor: [0, 123, 255],
                textColor: 255,
            },
        });

        doc.save('recurrence-data.pdf');
    };

    const isMobile = windowWidth < 768;

    // if (loading) {
    //     return <div className="flex justify-center items-center h-screen">Loading...</div>;
    // }

    if (error) {
        return <div className="flex justify-center items-center h-screen text-red-500">{error}</div>;
    }

    return (
        <div className="relative min-h-screen">
        {/* Loading Indicator */}
          {loading && (
               <div className="fixed inset-0 flex items-center justify-center z-50">
                 <div className="flex flex-col items-center p-3 bg-black border border-white rounded-2xl">
                   <FadeLoader 
                     height={15}
                     width={5}
                     color="#ffffff"
                     ariaLabel="loading"
                   />
                   <h1 className="mt-1 text-lg text-white">Please wait...</h1>
                 </div>
               </div>
             )}
             
        <div className="min-h-screen bg-white p-4 xl:px-60">
            <div className="text-2xl font-bold p-4">
                <span>Recurrence Plan Details</span>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center w-full mb-4">
                <div className="flex flex-col sm:flex-row items-center gap-4">
                    <div className="relative w-full sm:w-auto">
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchQuery}
                            onChange={handleSearch}
                            className="p-3 w-full sm:w-80 rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300 pl-10"
                        />
                        <CiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:justify-between items-center gap-4 mt-4 sm:mt-0">
                    <div className="flex gap-2 w-full sm:w-auto">
                        <button onClick={handleExcelExport} className="bg-green-500 text-white font-semibold py-2 px-4 rounded-full shadow-lg hover:bg-green-600 transition-all duration-300 flex items-center gap-2 flex-1 sm:flex-none justify-center">
                            <FaFileExcel className="text-xl" />
                            Excel
                        </button>
                        <button onClick={handlePdfExport} className="bg-red-500 text-white font-semibold py-2 px-4 rounded-full shadow-lg hover:bg-red-600 transition-all duration-300 flex items-center gap-2 flex-1 sm:flex-none justify-center">
                            <FaFilePdf className="text-xl" />
                            PDF
                        </button>
                    </div>
                    <div className="flex items-center gap-2 w-full sm:w-auto justify-center sm:justify-start mt-2 sm:mt-0">
                        <span>Show</span>
                        <select
                            value={entriesPerPage}
                            onChange={handleEntriesChange}
                            className="p-2 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value="All">All</option>
                        </select>
                        <span>entries</span>
                    </div>
                </div>
            </div>

            {/* Desktop view - Table */}
            {!isMobile && (
                <div className="overflow-x-auto mt-6 hidden sm:block">
                    <table className="min-w-full table-auto bg-white border border-gray-300 shadow-md rounded-lg">
                        <thead className="bg-blue-100">
                            <tr>
                                {/* <th className="px-4 py-2 border-b">#</th>    */}
                                <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort('recurring_id')}>
                                    <div className="flex items-center gap-2">
                                    Recurring ID {getSortIcon('recurring_id')}
                                    </div>
                                </th>
                                <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort('client_name')}>
                                    <div className="flex items-center gap-2">
                                        Client {getSortIcon('client_name')}
                                    </div>
                                </th>
                                <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort('recurrence_type')}>
                                    <div className="flex items-center gap-2">
                                        Recurrence Type {getSortIcon('recurrence_type')}
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedData.map((row, index) => (
                                <tr key={row.id} className="hover:bg-gray-50">
                                    {/* <td className="px-4 py-2 border-b">{startIndex + index + 1}</td>     */}
                                    <td className="px-4 py-2 border-b">{row.recurring_id}</td>
                                    <td className="px-4 py-2 border-b">{row.client_name}</td>
                                    <td className="px-4 py-2 border-b">{row.recurrence_type}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Mobile view - Cards */}
            {isMobile && (
                <div className="mt-6 sm:hidden">
                    {paginatedData.map((row, index) => (
                        <div key={row.id} className="bg-white shadow-md rounded-lg p-4 mb-4 border border-gray-200">
                            <div className="flex justify-between items-center border-b pb-2 mb-2">
                                <span className="font-bold">#{startIndex + index + 1}</span>
                                <span className="bg-blue-100 text-blue-800 rounded-full px-3 py-1 text-sm">
                                    {row.recurring_id}
                                </span>
                                <span className="bg-blue-100 text-blue-800 rounded-full px-3 py-1 text-sm">
                                    {row.recurrence_type}
                                </span>
                            </div>
                            <div className="py-2">
                                <div className="flex items-center mb-2">
                                    <span className="text-gray-600 w-1/3">Client:</span>
                                    <span className="font-medium">{row.client_name}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

<div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-4">
    <div className="text-sm text-gray-600 text-center sm:text-left w-full sm:w-auto">
        Showing {startIndex + 1} to {Math.min(startIndex + (entriesPerPage === "All" ? filteredData.length : entriesPerPage), filteredData.length)} of {filteredData.length} entries
    </div>
    <div className="flex items-center gap-2 flex-wrap justify-center">
        <button
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
        >
            Prev
        </button>

        {totalPages <= 5 ? (
            [...Array(totalPages).keys()].map((page) => (
                <button
                    key={page + 1}
                    onClick={() => handlePageChange(page + 1)}
                    className={`px-3 py-1 rounded ${currentPage === page + 1 ? "bg-blue-500 text-white" : "bg-gray-200 hover:bg-gray-300"}`}
                >
                    {page + 1}
                </button>
            ))
        ) : (
            <>
                {currentPage > 3 && (
                    <>
                        <button
                            onClick={() => handlePageChange(1)}
                            className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300"
                        >
                            1
                        </button>
                        {currentPage > 4 && <span className="px-3 py-1">...</span>}
                    </>
                )}

                {[...new Set([currentPage - 1, currentPage, currentPage + 1].filter(page => page > 0 && page <= totalPages))].map((page) => (
                    <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-1 rounded ${currentPage === page ? "bg-blue-500 text-white" : "bg-gray-200 hover:bg-gray-300"}`}
                    >
                        {page}
                    </button>
                ))}

                {currentPage < totalPages - 2 && (
                    <>
                        {currentPage < totalPages - 3 && <span className="px-3 py-1">...</span>}
                        <button
                            onClick={() => handlePageChange(totalPages)}
                            className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300"
                        >
                            {totalPages}
                        </button>
                    </>
                )}
            </>
        )}

        <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
        >
            Next
        </button>
    </div>

            </div>
        </div>
          
  </div>
    );
}

export default Client_pay_3;
// import { PiSortAscendingLight, PiSortDescendingLight } from "react-icons/pi";
// import { RiDeleteBin5Line } from "react-icons/ri";
// import { BiSolidEdit } from 'react-icons/bi';
// import DatePicker from "react-datepicker";
// import { CiSearch } from "react-icons/ci";
// import "react-datepicker/dist/react-datepicker.css";
// import { format, subDays, subMonths, startOfMonth, endOfMonth, startOfDay, endOfDay } from "date-fns";
// import { FaFileExcel, FaFilePdf } from 'react-icons/fa';
// import { HiOutlineMail } from "react-icons/hi";
// import { jsPDF } from 'jspdf';
// import * as XLSX from 'xlsx';
// import "jspdf-autotable";
// import Papa from 'papaparse';
// import { saveAs } from 'file-saver';

// function Client_pay_3() {
//     const [showDropdown, setShowDropdown] = useState(false);


//     // =======================

//     const [tableData, setTableData] = useState([
//         {
//             id: 1,
//             Client: "ServiceX",
//             Recurrence_Type: "sdaf",
//         },
//         {
//             id: 2,
//             Client: "ServiceY",
//             Recurrence_Type: "sdaf",


//         },
//         {
//             id: 3,
//             Client: "ServiceZ",
//             Recurrence_Type: "sdaf",
//         },
//         {
//             id: 4,
//             Client: "ServiceA",
//             Recurrence_Type: "sdaf",

//         },

//     ]
//     );



//     const [sortedTable, setSortedTable] = useState(tableData); // This will hold the sorted data
//     const [sortConfig, setSortConfig] = useState({ key: '', direction: '' });

//     // Sort function that updates sortedTable
//     const handleSort = (column) => {
//         let direction = 'asc';
//         if (sortConfig.key === column && sortConfig.direction === 'asc') {
//             direction = 'desc';
//         }
//         setSortConfig({ key: column, direction });

//         const sortedData = [...tableData].sort((a, b) => {
//             if (a[column] < b[column]) return direction === 'asc' ? -1 : 1;
//             if (a[column] > b[column]) return direction === 'asc' ? 1 : -1;
//             return 0;
//         });

//         setSortedTable(sortedData); // Update the sortedTable state
//     };

//     const getSortIcon = (column) => {
//         if (sortConfig.key !== column) {
//             return <PiSortAscendingLight className="text-gray-500" />;
//         }
//         return sortConfig.direction === 'asc' ? <PiSortAscendingLight /> : <PiSortDescendingLight />;
//     };

//     useEffect(() => {
//         setSortedTable(tableData); // Initialize sortedTable with tableData when component mounts
//     }, [tableData]);

//     const [formData, setFormData] = useState({
//         id: null,
//         Client: "",
//         Recurrence_Type: "",
//     });

//     const [searchQuery, setSearchQuery] = useState("");


//     const [entriesPerPage, setEntriesPerPage] = useState(10); // Number of entries per page
//     const [currentPage, setCurrentPage] = useState(1); // Current page number

//     // Filtered data based on search query
//     const filteredData = sortedTable.filter((row) =>
//         row.Client.toLowerCase().includes(searchQuery.toLowerCase()) ||
//         row.Recurrence_Type.toLowerCase().includes(searchQuery.toLowerCase())

//     );

//     // Paginated data based on the current page and entries per page
//     const startIndex = (currentPage - 1) * entriesPerPage;
//     const paginatedData = entriesPerPage === "All"
//         ? filteredData
//         : filteredData.slice(startIndex, startIndex + entriesPerPage);

//     const totalPages = Math.ceil(filteredData.length / entriesPerPage);

//     // Handle changes in the search query
//     const handleSearch = (e) => setSearchQuery(e.target.value);

//     // Handle changes in entries per page
//     const handleEntriesChange = (e) => {
//         setEntriesPerPage(e.target.value === "All" ? "All" : parseInt(e.target.value, 10));
//         setCurrentPage(1); // Reset to first page
//     };

//     // Pagination handlers
//     const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
//     const handleNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));
//     const handlePageChange = (page) => setCurrentPage(page);

//     const handleSubmit = (e) => {
//         e.preventDefault(); // Prevent the default form submission behavior

//         // Create new data object from the form
//         const newData = {
//             id: formData.id || tableData.length + 1, // Assign new ID if adding, else use the current ID for update
//             Client: formData.Client.trim(),
//             Recurrence_Type: formData.Recurrence_Type,


//         };

//         // If editing an existing entry, update that entry, else add a new one
//         if (formData.id) {
//             setTableData(tableData.map((row) =>
//                 row.id === formData.id ? newData : row
//             ));
//         } else {
//             setTableData([...tableData, newData]);
//         }

//         // Reset form data for the next entry
//         setFormData({
//             id: null,
//             Client: "",
//             Recurrence_Type: "",


//         });

//     };

//     const handleChange = (e) => {
//         const { name, value } = e.target;
//         setFormData({ ...formData, [name]: value });
//     };




//     // Handle deleting a staff entry
//     const handleDelete = (id) => {
//         setTableData(tableData.filter((row) => row.id !== id));
//     };

//     // Handle editing a staff entry
//     const handleEdit = (id) => {
//         // Find the staff entry by ID and populate the form with that data
//         const row = tableData.find((row) => row.id === id);
//         setFormData({
//             ...row,
//             Client: row.Client,
//             Recurrence_Type: row.Recurrence_Type,



//         });
//     };
// // ++++++++++++++++++++++++++++++++++++
// const handleExcelExport = () => {
//     // const ws = XLSX.utils.json_to_sheet(tableData); // Convert JSON to sheet
//     // const wb = XLSX.utils.book_new();
//     // XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
//     // const excelFile = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
//     // saveAs(new Blob([excelFile], { type: 'application/octet-stream' }), 'tableData.xlsx');

//     const columns = [
//         'S.No', 'Client', 'Recurrence_Type ',  
//       ];
  
//       // Table data
//       const rows = sortedTable.map((row, index) => [
//         index + 1,
//         row.Client,
//         row.Recurrence_Type,
        
         
//       ]);
  
//     // Create a worksheet from the data
//     const ws = XLSX.utils.aoa_to_sheet([columns, ...rows]);  
//     // Create a new workbook and add the worksheet
//     const wb = XLSX.utils.book_new();
//     XLSX.utils.book_append_sheet(wb, ws, 'Table Data');
  
//     // Save the workbook as an Excel file
//     XLSX.writeFile(wb, 'table-data.xlsx');
//   };

//   const handlePdfExport = () => {
 


//     const doc = new jsPDF();

//     // Table header
//     const columns = [
//       'S.No', 'Client', 'Recurrence_Type ',  
//     ];

//     // Table data
//     const rows = sortedTable.map((row, index) => [
//       index + 1,
//       row.Client,
//       row.Recurrence_Type,
      
       
//     ]);

//     // Add table to the PDF
//     doc.autoTable({
//       head: [columns],
//       body: rows,
//       startY: 20, // Start after 20mm from top
//       theme: 'grid', // Optional, can use 'striped', 'plain', etc.
//       margin: { horizontal: 10 },
//       headStyles: {
//         fillColor: [0, 123, 255], // Header background color
//         textColor: 255,
//       },
//     });

//     // Save the PDF
//     doc.save('table-data.pdf');
//   };  
//     return (
//         <div className="min-h-screen bg-white p-4  xl:px-60">
//             <div className="text-2xl font-bold p-4">
//                 <span> Recurrence Plan Details</span>
//             </div>

//             <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center w-full">
//                 {/* Left Side: Search, Buttons, and Show Entries */}
//                 <div className="flex flex-col sm:flex-row items-center">
//                     <div className="relative">
//                         <input
//                             type="text"
//                             placeholder="Search Staff..."
//                             value={searchQuery}
//                             onChange={handleSearch}
//                             className="p-3 lg:w-full sm:w-80 md:w-96   rounded-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300 pl-10"
//                         />
//                         <CiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
//                     </div>






//                 </div>

//                 {/* Excel and PDF Buttons */}
//                 <div className="flex sm:flex-row sm:justify-between items-center gap-1 mt-4 sm:mt-0">
//                     {/* Buttons Section */}
//                     <div className="flex sm:gap-4 lg:mr-3 sm:mr-0 items-center">
//                         <button onClick={handleExcelExport}     className="bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold text-sm sm:text-lg py-2 px-5 rounded-full shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-blue-700 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-2">
//                             <FaFileExcel className="text-2xl" />
//                             Excel
//                         </button>

//                         <button onClick={handlePdfExport} className="bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold  text-sm sm:text-lg  py-2 px-5 rounded-full shadow-lg hover:shadow-xl hover:from-red-600 hover:to-red-700 transition-all duration-300 transform hover:-translate-y-1 hover:scale-105 flex items-center gap-2">
//                             <FaFilePdf className="text-2xl" />
//                             PDF
//                         </button>
//                     </div>

//                     {/* Entries Per Page Dropdown */}
//                     <div className="flex items-center gap-1 sm:mt-0 mt-0">
//                         <span>Show</span>
//                         <select
//                             value={entriesPerPage}
//                             onChange={handleEntriesChange}
//                             className="p-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
//                         >
//                             <option value={10}>10</option>
//                             <option value={25}>25</option>
//                             <option value={50}>50</option>
//                             <option value="All">All</option>
//                         </select>
//                         <span>entries</span>
//                     </div>
//                 </div>
//             </div>

//             {/* Table */}
//             <div className="overflow-x-auto mt-6">
//                 <table className="min-w-full table-auto bg-white border border-gray-300 shadow-md rounded-lg hidden sm:table">
//                     <thead className="bg-blue-100 text-left">
//                         <tr>
//                             <th className="px-4 py-2 border-b cursor-pointer">#</th>
//                             <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort('Client')}>
//                                 <div className="flex items-center gap-2">
//                                     Client {getSortIcon('Client')}
//                                 </div>
//                             </th>
//                             <th className="px-4 py-2 border-b cursor-pointer" onClick={() => handleSort('Recurrence_Type')}>
//                                 <div className="flex items-center gap-2">
//                                     Recurrence Type {getSortIcon('Recurrence_Type')}
//                                 </div>
//                             </th>
//                             <th className="px-4 py-2 border-b">Actions</th>
//                         </tr>
//                     </thead>
//                     <tbody>
//                         {sortedTable.length > 0 ? (
//                             paginatedData.map((row, index) => (
//                                 <tr key={row.id} className="hover:bg-gray-50">
//                                     <td className="px-4 py-2 border-b">{index + 1}</td>
//                                     <td className="px-4 py-2 border-b">{row.Client}</td>
//                                     <td className="px-4 py-2 border-b">{row.Recurrence_Type}</td>
//                                     <td className="px-4 py-2 border-b">
//                                         <button
//                                             onClick={() => handleEdit(row.id)}
//                                             className="text-green-700 hover:text-green-900"
//                                         >
//                                             <BiSolidEdit className="text-3xl" />
//                                         </button>
//                                         <button
//                                             onClick={() => handleDelete(row.id)}
//                                             className="text-red-500 hover:text-red-600 ml-2"
//                                         >
//                                             <RiDeleteBin5Line className="text-3xl" />
//                                         </button>
//                                     </td>
//                                 </tr>
//                             ))
//                         ) : (
//                             <tr>
//                                 <td colSpan="9" className="text-center py-4">No data available</td>
//                             </tr>
//                         )}
//                     </tbody>
//                 </table>

//                 <div className="sm:hidden">
//                     {sortedTable.length > 0 ? (
//                         paginatedData.map((row, index) => (
//                             <div
//                                 key={row.id}
//                                 className="bg-white border border-gray-300 shadow-md rounded-lg mb-4 p-4"
//                             >

//                                 <div className="mb-3">
//                                     <span className="font-semibold"> Client .: </span>{row.Client}
//                                 </div>
//                                 <div className="mb-3">
//                                     <span className="font-semibold"> Recurrence_Type .: </span>{row.Recurrence_Type}
//                                 </div>
//                                 <div className="flex justify-between items-center">
//                                     <button
//                                         onClick={() => handleEdit(row.id)}
//                                         className="text-green-700 hover:text-green-900"
//                                     >
//                                         <BiSolidEdit className="text-2xl" />
//                                     </button>
//                                     <button
//                                         onClick={() => handleDelete(row.id)}
//                                         className="text-red-500 hover:text-red-600"
//                                     >
//                                         <RiDeleteBin5Line className="text-2xl" />
//                                     </button>
//                                 </div>



//                             </div>
//                         ))
//                     ) : (
//                         <div className="text-center text-gray-500">No data found</div>
//                     )}
//                 </div>
//             </div>
//             <div className="flex flex-col sm:flex-row justify-between items-center mt-4">
//                 <div className="text-sm text-gray-600 mb-4 sm:mb-0">
//                     <span>Showing </span><span>1</span><span> to </span><span>5</span><span> of </span><span>2</span><span> entries</span>
//                 </div>

//                 <div className="flex items-center gap-4 sm:gap-8">
//                     {/* Pagination Controls */}
//                     {entriesPerPage !== "All" && (
//                         <div className="flex items-center">
//                             <button
//                                 onClick={handlePrevPage}
//                                 disabled={currentPage === 1}
//                                 className="px-4 py-2 bg-gray-300 rounded-l hover:bg-gray-400 disabled:opacity-50"
//                             >
//                                 Previous
//                             </button>
//                             {[...Array(totalPages).keys()].map((page) => (
//                                 <button
//                                     key={page + 1}
//                                     onClick={() => handlePageChange(page + 1)}
//                                     className={`px-4 py-2 border ${currentPage === page + 1
//                                         ? "bg-blue-500 text-white"
//                                         : "bg-gray-200 hover:bg-gray-300"
//                                         }`}
//                                 >
//                                     {page + 1}
//                                 </button>
//                             ))}
//                             <button
//                                 onClick={handleNextPage}
//                                 disabled={currentPage === totalPages}
//                                 className="px-4 py-2 bg-gray-300 rounded-r hover:bg-gray-400 disabled:opacity-50"
//                             >
//                                 Next
//                             </button>
//                         </div>
//                     )}



//                 </div>
//             </div>

//         </div >
//     )
// }

// export default Client_pay_3
